import { useDateFnsLocale } from "@/lib/i18n/format"
import { memo } from "react"
import { format } from "date-fns"
import { SearchHit } from "@/lib/searchResultParser"
import { extractVhostFromJid } from "@/jidUtils"
import { formatMessageTimestamp } from "@/lib/messageDate"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { useAppSelector } from "@/reducers/hooks"
import { participantPhotoToSrc } from "@/components/AvatarImage"
import { profilesByUniqueName } from "@/reducers/profilesSliceSelectors"
import ChatHelpers from "@/lib/chatHelpers"
import { Card, CardContent } from "@/components/ui/card"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import SearchMessageBubbleBody from "./SearchMessageBubbleBody"
import SearchHighlightedText from "./SearchHighlightedText"

function SearchMessageRow({
  onGoToDetails,
  messageHit,
  showChatTitle = true,
  showVhost = false,
}: {
  onGoToDetails: () => void
  messageHit: SearchHit
  showChatTitle?: boolean
  showVhost?: boolean
}) {
  const dateLocale = useDateFnsLocale()
  const date = new Date(messageHit.document.timestamp / 1000)
  const sender = messageHit.document.sender
  const profile = useAppSelector(profilesByUniqueName).get(sender) ?? undefined
  const fromSlickAI = ChatHelpers.isChatWithSlickAI(
    profile?.jid ?? `${sender.replace(/\s/g, "")}@${messageHit.document.host}`,
  )
  const initials = sender
    .split(/\s+/)
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase()
  return (
    <Card
      role="button"
      tabIndex={0}
      data-search-message-id={messageHit.document.id}
      onClick={onGoToDetails}
      onKeyDown={(event) => {
        if (
          event.currentTarget === event.target &&
          (event.key === "Enter" || event.key === " ")
        ) {
          event.preventDefault()
          onGoToDetails()
        }
      }}
      className="w-full cursor-pointer rounded-xl bg-card shadow-none transition-colors hover:bg-accent/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <CardContent className="flex flex-col gap-3 p-4">
        {showChatTitle && (
          <p
            className="text-sm text-muted-foreground"
            data-testid="search-result-chat"
          >
            # {messageHit.document.chat}
            {showVhost && (
              <span
                className="ml-2 break-all"
                data-testid="search-result-vhost"
              >
                {extractVhostFromJid(messageHit.document.chat_jid)}
              </span>
            )}
          </p>
        )}
        <div className="flex items-start gap-3">
          <Avatar className="size-8 shrink-0">
            <AvatarImage
              src={
                fromSlickAI
                  ? "/slickAvatar.svg"
                  : participantPhotoToSrc(profile?.photo)
              }
              alt={sender}
            />
            <AvatarFallback
              className={
                profile?.role === "driver"
                  ? "bg-blue-100 text-xs text-blue-900 dark:bg-blue-950 dark:text-blue-100"
                  : "bg-secondary text-xs text-secondary-foreground"
              }
            >
              {initials}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1 space-y-1">
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-sm">
              <span className="font-medium text-foreground">
                {messageHit.highlight?.sender?.value ? (
                  <SearchHighlightedText
                    html={messageHit.highlight.sender.value}
                  />
                ) : (
                  sender
                )}
              </span>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <time
                      dateTime={date.toISOString()}
                      className="whitespace-nowrap text-xs text-muted-foreground"
                    >
                      {formatMessageTimestamp(date)}
                    </time>
                  </TooltipTrigger>
                  <TooltipContent>
                    {format(date, "EEEE, PPP HH:mm", { locale: dateLocale })}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <div className="whitespace-pre-wrap break-words text-sm text-foreground">
              <SearchMessageBubbleBody messageHit={messageHit} />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

export default memo(SearchMessageRow)
